/**
 * Theme primitives shared by ThemeContext and any code that needs to read
 * the persisted preference before React mounts (e.g. inline boot script).
 */
export type ThemePreference = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "certain.theme";
const VALID_PREFERENCES: ThemePreference[] = ["light", "dark", "system"];

/** Read the user's saved preference from localStorage. Defaults to `dark`
 * (the founder-locked default per Batch 27f) when nothing is set. */
export function readStoredPreference(): ThemePreference {
  if (typeof window === "undefined") return "dark";
  const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (raw && (VALID_PREFERENCES as string[]).includes(raw)) {
    return raw as ThemePreference;
  }
  return "dark";
}

/** Resolve a preference to the actual effective theme (`light` | `dark`). */
export function resolvePreference(pref: ThemePreference): EffectiveTheme {
  if (pref === "system") {
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
    return "light";
  }
  return pref;
}

/** Apply the resolved theme via a `data-theme` attribute on <html>.
 *
 * 30f: switched from class-based (`.dark`) to attribute-based
 * (`data-theme="dark|light"`). Tailwind's darkMode is configured to read
 * `[data-theme="dark"]`, so all existing `dark:*` variants keep working.
 * The single-attribute swap is instant and avoids re-render flicker. */
export function applyTheme(effective: EffectiveTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", effective);
}
